// Field schema mirroring the tour guide's tracking sheet.
// type: text | number | bool | textarea | badge
export const FIELDS = [
  { key: "guest_name", label: "Guest Name", type: "text", primary: true },
  { key: "booking_status", label: "Booking Status", type: "status" },
  { key: "tour_date", label: "Tour Date", type: "dates" },
  { key: "days", label: "Tour Days", type: "number" },
  { key: "resort_meet", label: "Meeting Location", type: "text" },
  { key: "meeting_time", label: "Meeting Time", type: "text" },
  { key: "shift_start", label: "Shift Start", type: "text" },
  { key: "shift_end", label: "Shift End", type: "text" },
  { key: "tour_type", label: "Type", type: "select", options: ["Premium", "PEP"] },
  { key: "cost_type", label: "Cost Type", type: "select", options: ["External", "Internal", "Internal - Club 33", "Internal - Club 33 Gifted", "Internal - Event"] },
  { key: "cost_per_hour", label: "Cost / Hour", type: "number", money: true },
  { key: "promo", label: "Promo Discount", type: "promo" },
  { key: "total_hours", label: "Total Hours", type: "number", step: 0.25 },
  { key: "comp_time", label: "Comp Time (hrs)", type: "number", step: 0.25 },
  { key: "rating", label: "Rating", type: "rating" },
  { key: "gratuity", label: "Gratuity", type: "number", money: true },
  { key: "tip_percent", label: "Tip %", type: "number", percent: true, computed: true },
  { key: "tip_type", label: "Gratuity Type", type: "select", options: ["Cash", "Electronic", "Mix", "Check"] },
  { key: "avg_per_guest", label: "Avg Gratuity / Day", type: "number", money: true, computed: true },
  { key: "return_trip", label: "Return", type: "bool" },
  { key: "co_guide", label: "Co-Guide", type: "text" },
  { key: "guest_food", label: "Guest Food", type: "text" },
  { key: "viewings", label: "Viewings", type: "multiselect" },
  { key: "host_again", label: "Host Again?", type: "bool" },
  { key: "retail_value", label: "Retail Value", type: "number", money: true, computed: true },
  { key: "notes", label: "Notes", type: "textarea" },
];

// Tip / earnings fields hidden everywhere when Financial Privacy is on (the guide's own money).
// NOTE: cost_per_hour (hourly rate) and retail_value (what the guest pays Disney) are NOT hidden.
export const FINANCIAL_KEYS = ["gratuity", "tip_percent", "tip_type", "avg_per_guest"];

// --- Per-user field customization (hide base fields + user-added custom fields) ---
// Core fields the app depends on — never hideable.
export const CORE_LOCKED_KEYS = ["guest_name", "tour_date", "booking_status"];
export const CUSTOM_FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "number", label: "Number" },
  { value: "bool", label: "Yes / No" },
  { value: "select", label: "Dropdown" },
  { value: "textarea", label: "Long text" },
  { value: "date", label: "Date" },
];

// Normalize a stored custom-field def into the same shape base FIELDS use, so all
// renderers (add/edit dialog, detail grid, list) treat it like any other field.
export const normalizeCustomField = (c) => ({
  key: c.key,
  label: c.label,
  type: c.type,
  options: c.type === "select" ? (c.options || []) : undefined,
  money: c.type === "number" ? !!c.money : undefined,
  percent: c.type === "number" ? !!c.percent : undefined,
  custom: true,
  section: c.section === "general" ? "general" : "custom",
  in_list: !!c.in_list,
  ai_extract: !!c.ai_extract,
  ai_hint: c.ai_hint || "",
});

// Base fields (minus hidden) followed by the user's custom fields.
export const buildEffectiveFields = (hidden = [], custom = []) => {
  const hiddenSet = new Set(hidden);
  const base = FIELDS.filter((f) => !hiddenSet.has(f.key));
  const customs = (custom || []).map(normalizeCustomField);
  return [...base, ...customs];
};

// Promo discount: a tour sheet marked "DVC Promo" or "Club Level Promo" gets a flat
// 20% off the hourly retail rate. Detected by AI from the sheet body; user can override.
export const PROMO_OPTIONS = ["DVC Promo", "Club Level Promo"];
export const PROMO_DISCOUNT = 0.2;

// The per-day rate always stays the FULL (non-discounted) rate. A promo only reduces the
// TOTAL RETAIL the guest pays. promoFactor is what retail is multiplied by.
export const promoFactor = (promo) => (PROMO_OPTIONS.includes(promo) ? 1 - PROMO_DISCOUNT : 1);

// Kept for back-compat with existing import paths; rates are no longer mutated (stay full).
export const applyPromoDiscount = (fields) => ({ ...fields });

// Columns shown in the main database table.
export const TABLE_COLUMNS = [
  "guest_name", "tour_date", "gratuity",
];

export const statusAbbrev = (s) => ({
  Current: "Now",
  Complete: "Done",
  "Past (Update)": "Update",
  "Future (Scheduled)": "Sched",
  "Future (Request)": "Request",
  "Future (Possible)": "Request",
}[s] || s || "—");

// True when a tour is a tentative "Future Request" (new nomenclature; accepts the legacy value).
export const isFutureRequest = (t) => {
  const s = t?.status_override || t?.booking_status;
  return s === "Future (Request)" || s === "Future (Possible)";
};

export const formatValue = (field, value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (field.type === "dates") {
    const arr = Array.isArray(value) ? value : [value];
    return arr.length ? arr.join(", ") : "—";
  }
  if (field.type === "bool") return value ? "Yes" : "No";
  if (field.money) return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  if (field.percent) return `${value}%`;
  return String(value);
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const parseMDY = (s) => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(s).trim());
  if (!m) return null;
  const mo = +m[1], da = +m[2], yr = +m[3];
  const dt = new Date(yr, mo - 1, da);
  if (dt.getMonth() !== mo - 1 || dt.getDate() !== da) return null;
  return { y: yr, mo, d: da, t: dt.getTime() };
};

// Compact date display: consecutive dates collapse into a range (e.g. "Jan 8–10, 2026").
// Returns null if any date can't be parsed so the caller can fall back to raw chips.
export const formatTourDates = (dates) => {
  const arr = Array.isArray(dates) ? dates : (dates ? [dates] : []);
  if (!arr.length) return null;
  const parsed = arr.map(parseMDY);
  if (parsed.some((p) => !p)) return null;
  parsed.sort((a, b) => a.t - b.t);
  const uniq = [];
  parsed.forEach((p) => { if (!uniq.length || uniq[uniq.length - 1].t !== p.t) uniq.push(p); });
  const DAY = 86400000;
  const groups = [];
  let cur = [uniq[0]];
  for (let i = 1; i < uniq.length; i++) {
    if (uniq[i].t - cur[cur.length - 1].t === DAY) cur.push(uniq[i]);
    else { groups.push(cur); cur = [uniq[i]]; }
  }
  groups.push(cur);
  const sameYearAll = uniq.every((p) => p.y === uniq[0].y);
  const withY = (p) => `${MONTHS[p.mo - 1]} ${p.d}, ${p.y}`;
  const noY = (p) => `${MONTHS[p.mo - 1]} ${p.d}`;
  const fmtGroup = (g) => {
    const a = g[0], b = g[g.length - 1];
    if (g.length === 1) return sameYearAll ? noY(a) : withY(a);
    if (a.y !== b.y) return `${withY(a)} – ${withY(b)}`;
    if (a.mo === b.mo) return `${MONTHS[a.mo - 1]} ${a.d}–${b.d}`;
    return `${MONTHS[a.mo - 1]} ${a.d} – ${MONTHS[b.mo - 1]} ${b.d}`;
  };
  let out = groups.map(fmtGroup).join(" · ");
  if (sameYearAll) out += `, ${uniq[0].y}`;
  return out;
};

// Color band for a gratuity based on tip % of retail: >=10% green, <=5% red, between = amber/muted.
export const gratuityBand = (tipPercent) => {
  if (tipPercent === null || tipPercent === undefined || tipPercent === "") return null;
  const n = Number(tipPercent);
  if (!isFinite(n)) return null;
  if (n >= 10) return "green";
  if (n <= 5) return "red";
  return "amber";
};

// Shared text-color classes so the tip color cue looks identical in every view.
export const gratuityColor = (tipPercent) => {
  const b = gratuityBand(tipPercent);
  return b === "green" ? "text-emerald-300" : b === "red" ? "text-rose-300" : b === "amber" ? "text-amber-300" : "text-zinc-100";
};
export const gratuityPctColor = (tipPercent) => {
  const b = gratuityBand(tipPercent);
  return b === "green" ? "text-emerald-400/70" : b === "red" ? "text-rose-300/70" : b === "amber" ? "text-amber-300/70" : "text-zinc-400";
};

// Person-level fields (roll up into a Guest profile). Auto-pulled from the sheet.
export const PROFILE_FIELDS = [
  { key: "city", label: "City / Town", type: "text" },
  { key: "state", label: "State", type: "text" },
  { key: "country", label: "Country", type: "text" },
  { key: "mdx_email", label: "MDX Email", type: "text" },
  { key: "phone", label: "Phone Number", type: "text" },
];

const lastName = (name) => {
  if (!name) return "Guest";
  const n = String(name).trim();
  if (n.includes(",")) return n.split(",")[0].trim();
  const parts = n.split(/\s+/);
  return parts[parts.length - 1] || n;
};

// Collapse tour-type variants to the two real types. "Premium VIP" == "Premium" (drop VIP).
export const normalizeTourType = (v) => {
  if (!v) return v;
  const u = String(v).toUpperCase();
  if (u.includes("PEP")) return "PEP";
  if (u.includes("PREMIUM")) return "Premium";
  return v;
};

const _splitPerson = (n) => {
  const s = String(n || "").trim();
  if (s.includes(",")) { const [last, ...rest] = s.split(","); return [last.trim().toLowerCase(), rest.join(",").trim().toLowerCase()]; }
  const p = s.split(/\s+/);
  if (p.length >= 2) return [p[p.length - 1].toLowerCase(), p.slice(0, -1).join(" ").toLowerCase()];
  return [s.toLowerCase(), ""];
};
const _firstCompatible = (a, b) => {
  const x = String(a || "").toLowerCase().replace(/[^a-z]/g, "");
  const y = String(b || "").toLowerCase().replace(/[^a-z]/g, "");
  if (!x || !y) return false;
  if (x === y || x.startsWith(y) || y.startsWith(x)) return true;
  let i = 0; while (i < Math.min(x.length, y.length) && x[i] === y[i]) i += 1;
  return i >= 3; // Kathy/Katherine share "kath"
};
// Same person? (e.g. Kathy ~ Katherine) — same last name + nickname-compatible first name.
export const sameGuestName = (a, b) => {
  if (!a || !b) return false;
  const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
  if (norm(a) === norm(b)) return true;
  const [al, af] = _splitPerson(a);
  const [bl, bf] = _splitPerson(b);
  return !!(al && bl && norm(al) === norm(bl) && _firstCompatible(af, bf));
};

// Reorder a guest list so families read together: keep the adults in their original order,
// but pull each child up to sit right after the LAST adult who shares their last name. This
// surfaces the individual family sub-groups inside a bigger party. Children whose last name
// matches no adult are kept (in order) at the end. Adults' relative order never changes.
const _guestLastName = (g) => {
  const n = String(g?.name || "").trim();
  if (!n) return "";
  if (n.includes(",")) return n.split(",")[0].trim().toLowerCase();
  const parts = n.split(/\s+/);
  return parts[parts.length - 1].toLowerCase();
};
const _isChildGuest = (g) => {
  const a = g?.age;
  return a !== "" && a !== null && a !== undefined && !isNaN(Number(a)) && Number(a) < 18;
};
export const groupGuestsByFamily = (list) => {
  const rows = Array.isArray(list) ? list : [];
  if (rows.length < 2) return rows;
  const adults = rows.filter((g) => !_isChildGuest(g));
  const children = rows.filter((g) => _isChildGuest(g));
  if (adults.length === 0 || children.length === 0) return rows;
  // Last adult index per last name (children attach after the last same-name adult).
  const lastAdultIdx = {};
  adults.forEach((g, i) => { lastAdultIdx[_guestLastName(g)] = i; });
  const afterAdult = adults.map(() => []);
  const orphans = [];
  children.forEach((c) => {
    const ln = _guestLastName(c);
    if (ln && ln in lastAdultIdx) afterAdult[lastAdultIdx[ln]].push(c);
    else orphans.push(c);
  });
  const out = [];
  adults.forEach((g, i) => { out.push(g); afterAdult[i].forEach((c) => out.push(c)); });
  orphans.forEach((c) => out.push(c));
  return out;
};


// Build the date portion of a tour title from one or more dates.
//  - single day:              M/D/YY
//  - consecutive multi-day:   M/D-D/YY   (or M/D-M/D/YY across months)
//  - split days:              M/D & D/YY (month repeated only when it changes)
//  - mixed:                   M/D-D & D/YY
const shortDate = (td) => {
  const arr = Array.isArray(td) ? td : td ? [td] : [];
  const parsed = arr
    .map((s) => {
      const m = String(s).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      if (!m) return null;
      let y = +m[3];
      if (y < 100) y += 2000;
      return { m: +m[1], d: +m[2], y, t: new Date(y, +m[1] - 1, +m[2]).getTime() };
    })
    .filter(Boolean)
    .sort((a, b) => a.t - b.t);
  if (!parsed.length) {
    const d = Array.isArray(td) ? td[0] : td;
    return d ? String(d) : "";
  }
  const uniq = [];
  parsed.forEach((p) => { if (!uniq.length || uniq[uniq.length - 1].t !== p.t) uniq.push(p); });
  const DAY = 86400000;
  const groups = [[uniq[0]]];
  for (let i = 1; i < uniq.length; i++) {
    if (uniq[i].t - uniq[i - 1].t === DAY) groups[groups.length - 1].push(uniq[i]);
    else groups.push([uniq[i]]);
  }
  let lastMonth = null;
  const tokens = groups.map((g) => {
    const start = g[0];
    const startStr = start.m !== lastMonth ? `${start.m}/${start.d}` : `${start.d}`;
    lastMonth = start.m;
    if (g.length === 1) return startStr;
    const end = g[g.length - 1];
    const endStr = end.m !== lastMonth ? `${end.m}/${end.d}` : `${end.d}`;
    lastMonth = end.m;
    return `${startStr}-${endStr}`;
  });
  return `${tokens.join(" & ")}/${String(uniq[uniq.length - 1].y).slice(-2)}`;
};

// Auto tour label: "LastName (dates)" spanning every tour date (see shortDate).
// Gifted Club 33 tours append "(Gifted to RecipientLast)".
export const autoTourTitle = (g) => {
  const ln = lastName(g.guest_name);
  const sd = shortDate(g.tour_date);
  let title = sd ? `${ln} (${sd})` : ln;
  if (g.gifted_to && String(g.gifted_to).trim()) {
    title += ` (Gifted to ${lastName(g.gifted_to)})`;
  }
  return title;
};

// Effective tour name: manual tour_name if set, else the auto label.
export const tourTitle = (g) => {
  if (g.tour_name && String(g.tour_name).trim()) return String(g.tour_name).trim();
  return autoTourTitle(g);
};

const num = (x) =>
  x === "" || x === null || x === undefined || isNaN(Number(x)) ? 0 : Number(x);

// App-wide settings (defaults for new tours), stored in localStorage.
export const getSettings = () => {
  try {
    const s = JSON.parse(localStorage.getItem("appSettings") || "{}");
    return { costPerHour: 650, tourHours: 7, ...s };
  } catch {
    return { costPerHour: 650, tourHours: 7 };
  }
};
export const saveSettings = (s) => localStorage.setItem("appSettings", JSON.stringify(s));

// Badge color for tour type: Premium = brand maroon; PEP = neutral (keeps the palette calm).
export const tourTypeColor = (t) =>
  ({
    Premium: "bg-[#9B2226]/15 text-[#E0575B]",
    PEP: "bg-zinc-700/40 text-zinc-300",
  }[t] || "bg-zinc-700/40 text-zinc-300");

// Show / parade "viewings" — base options (custom ones are stored server-side).
export const VIEWING_OPTIONS = [
  "HEA", "FoF", "Starlight", "Fant", "Luminous", "FOLK",
  "Frozen Sing", "Indy", "BatB", "Nemo", "Spec. FW", "Spec. Parade",
];
export const VIEWING_BADGE = "bg-zinc-800 text-zinc-300";
export const viewingColor = () => VIEWING_BADGE;

// Normalize a viewings value (legacy comma string or array) to an array.
export const toViewingsArray = (v) => {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === "string" && v.trim()) return v.split(",").map((s) => s.trim()).filter(Boolean);
  return [];
};

// Auto-calculated fields (post-tour):
//  retail_value   = Cost/Hour × (Total Hours − Comp Time)
//  tip_percent    = Gratuity ÷ Retail Value × 100
//  avg_per_guest  = Gratuity ÷ Days (average gratuity per day)
export const computeDerived = (v) => {
  const dayShifts = Array.isArray(v.day_shifts) ? v.day_shifts : [];
  const fullRetail = dayShifts.length
    ? sumRetail(dayShifts, num(v.cost_per_hour))
    : +(num(v.cost_per_hour) * (num(v.total_hours) - num(v.comp_time))).toFixed(2);
  // Full retail is at the (non-discounted) rate; a promo reduces only what the guest pays.
  const retail_value = +(fullRetail * promoFactor(v.promo)).toFixed(2);
  const tip_percent = retail_value > 0 ? +((num(v.gratuity) / retail_value) * 100).toFixed(1) : 0;
  const days = num(v.days);
  const avg_per_guest = days > 0 ? +(num(v.gratuity) / days).toFixed(2) : num(v.gratuity);
  return { retail_value, tip_percent, avg_per_guest };
};

// Defaults applied only when STARTING a new tour (empty fields).
// Total Hours auto-scales to (days × 7); min 7 for a single-day tour.
export const withNewTourDefaults = (f) => {
  const out = { ...f };
  const st = getSettings();
  // Anchor the day count to the number of extracted tour dates when present, so `days` and
  // `total_hours` never disagree with tour_date (a mismatch made day-1 absorb the whole
  // multi-day total, e.g. 14h instead of 7h/day). Fall back to `days`, then 1.
  const dateCount = Array.isArray(out.tour_date) ? out.tour_date.length : (out.tour_date ? 1 : 0);
  const d = Number(out.days);
  const daysNum = dateCount >= 1 ? dateCount : (!isNaN(d) && d >= 1 ? d : 1);
  out.days = daysNum;
  if (out.total_hours === undefined || out.total_hours === null || out.total_hours === "") out.total_hours = daysNum * (st.tourHours || 7);
  if (out.comp_time === undefined || out.comp_time === null || out.comp_time === "") out.comp_time = 0;
  if (out.host_again === undefined || out.host_again === null) out.host_again = true;
  if (!out.tour_type || !["Premium", "PEP"].includes(out.tour_type)) out.tour_type = "Premium";
  if (!out.cost_type) out.cost_type = "External";
  return out;
};

// Total hours for a given number of days (uses the default tour length per day).
export const hoursForDays = (days) => {
  const d = Number(days);
  const per = getSettings().tourHours || 7;
  return (!isNaN(d) && d >= 1 ? d : 1) * per;
};

// Add (or subtract) hours to a time string; returns "H:MM AM/PM".
export const addHoursToTime = (t, hours) => {
  if (!t) return "";
  const m = String(t).trim().toUpperCase().match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/);
  if (!m) return "";
  let h = parseInt(m[1], 10);
  const mi = parseInt(m[2] || "0", 10);
  const ap = m[3];
  if (ap === "PM" && h !== 12) h += 12;
  else if (ap === "AM" && h === 12) h = 0;
  let total = h * 60 + mi + Math.round(Number(hours) * 60);
  total = ((total % 1440) + 1440) % 1440;
  const H = Math.floor(total / 60), M = total % 60;
  return `${H % 12 || 12}:${String(M).padStart(2, "0")} ${H < 12 ? "AM" : "PM"}`;
};

// Meeting time = shift start + 1 hour.
export const plusOneHour = (t) => addHoursToTime(t, 1);

// Shift start = meeting time - 1 hour.
export const minusOneHour = (t) => addHoursToTime(t, -1);

// Shift end = shift start + (total hours + 2 buffer).
export const shiftEndFromStart = (start, totalHours) => {
  if (!start) return "";
  const th = totalHours === "" || totalHours === null || totalHours === undefined || isNaN(Number(totalHours)) ? 0 : Number(totalHours);
  return addHoursToTime(start, th + 2);
};

// Default per-day shift for new/imported tours: 6:15 AM–3:15 PM (9-hour shift; → 7:15 AM pickup via +1h rule).
export const DEFAULT_SHIFT = { start: "6:15 AM", end: "3:15 PM" };

// Kept for back-compat; day 1 now uses the same 6:15 AM default as later days.
export const SUBSEQUENT_DAY_START = "6:15 AM";
export const subsequentDayShift = () => ({
  start: SUBSEQUENT_DAY_START,
  end: shiftEndFromStart(SUBSEQUENT_DAY_START, getSettings().tourHours || 7),
});

// Parse a "h:mm AM/PM" string to minutes-since-midnight, or null.
export const timeToMinutes = (t) => {
  if (!t) return null;
  const m = String(t).trim().toUpperCase().match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mi = parseInt(m[2] || "0", 10);
  const ap = m[3];
  if (ap === "PM" && h !== 12) h += 12;
  else if (ap === "AM" && h === 12) h = 0;
  return h * 60 + mi;
};

// Hours between two time strings (overnight-safe), rounded to 2 dp. This is the raw SHIFT length.
export const hoursBetween = (start, end) => {
  const s = timeToMinutes(start), e = timeToMinutes(end);
  if (s === null || e === null) return 0;
  let diff = e - s;
  if (diff <= 0) diff += 1440;
  return +(diff / 60).toFixed(2);
};

// Billable TOUR hours for a shift: the shift starts 1h before and ends 1h after the tour,
// so tour hours = shift length − 2 (e.g. a 9 AM–6 PM shift = a 7-hour tour). Never below 0.
export const tourHoursBetween = (start, end) => {
  const shift = hoursBetween(start, end);
  return shift > 0 ? +Math.max(shift - 2, 0).toFixed(2) : 0;
};

// Pickup-based tour hours: the timer starts at the PICKUP time (not the shift clock-in, which
// may be earlier for prep) and the tour ends 1h before the shift ends. So tour hours =
// (shift end − 1h) − pickup. Early arrivals never inflate the tour length.
export const tourHoursFromPickup = (pickup, end) => {
  const h = hoursBetween(pickup, end);
  return h > 0 ? +Math.max(h - 1, 0).toFixed(2) : 0;
};

// Derived (non-manual) tour hours for a day: prefer the pickup-based timer when a pickup +
// end are present, otherwise fall back to the shift-length rule.
export const derivedDayHours = (d) =>
  (d && d.meet_time && d.end) ? tourHoursFromPickup(d.meet_time, d.end) : tourHoursBetween(d && d.start, d && d.end);

// Billable tour hours for one day: a manual override (d.hours + d.hours_manual) wins, else derived.
export const dayTourHours = (d) =>
  (d && d.hours_manual && d.hours !== undefined && d.hours !== null && d.hours !== "")
    ? Number(d.hours)
    : derivedDayHours(d);

// Total (billable tour) hours across all per-day shifts.
export const sumShiftHours = (dayShifts) =>
  +((dayShifts || []).reduce((acc, d) => acc + (dayTourHours(d) || 0), 0)).toFixed(2);

// Total comp time across all per-day shifts.
export const sumComp = (dayShifts) =>
  +((dayShifts || []).reduce((acc, d) => acc + (Number(d.comp) || 0), 0)).toFixed(2);

// Per-day hourly rate: a manual override (d.rate) if set, else the tour's fallback rate.
export const dayRate = (d, fallback = 0) =>
  (d && d.rate !== undefined && d.rate !== null && d.rate !== "")
    ? Number(d.rate)
    : (Number(fallback) || 0);

// Per-day retail = rate × billable tour hours (tour hours − comp), never below 0.
export const dayRetail = (d, fallback = 0) => {
  const billable = Math.max((dayTourHours(d) || 0) - (Number(d && d.comp) || 0), 0);
  return +(dayRate(d, fallback) * billable).toFixed(2);
};

// Total retail across all per-day shifts (each day priced at its own rate).
export const sumRetail = (dayShifts, fallback = 0) =>
  +((dayShifts || []).reduce((acc, d) => acc + dayRetail(d, fallback), 0)).toFixed(2);

// Weighted average hourly rate = total retail ÷ total billable hours (hours − comp).
export const avgHourlyRate = (dayShifts, fallback = 0) => {
  const billable = +((sumShiftHours(dayShifts) || 0) - (sumComp(dayShifts) || 0)).toFixed(2);
  if (billable > 0) return +(sumRetail(dayShifts, fallback) / billable).toFixed(2);
  return Number(fallback) || 0;
};

// Derive the tour's top-level mirror fields from its per-day shifts (day 1 location/time; summed totals).
// cost_per_hour is stored as the weighted AVERAGE of the per-day rates (fallbackRate = prior rate).
export const rollupShifts = (dayShifts = [], fallbackRate = 0) => {
  const d0 = dayShifts[0] || {};
  return {
    resort_meet: d0.meet_location || "",
    meeting_time: d0.meet_time || (d0.start ? plusOneHour(d0.start) : ""),
    shift_start: d0.start || "",
    shift_end: d0.end || "",
    total_hours: sumShiftHours(dayShifts),
    comp_time: sumComp(dayShifts),
    cost_per_hour: avgHourlyRate(dayShifts, fallbackRate),
  };
};

// Align a day_shifts array to the given tour dates: reuse existing entries by date, else use
// `fallback` for day 1 and `subsequentFallback` (if given) for any day after day 1.
export const syncDayShifts = (dates, existing = [], fallback = DEFAULT_SHIFT, subsequentFallback = null) => {
  const arr = Array.isArray(dates) ? dates : dates ? [dates] : [];
  const byDate = {};
  (existing || []).forEach((d) => { if (d && d.date) byDate[d.date] = d; });
  return arr.map((dt, i) => {
    if (byDate[dt]) return byDate[dt];
    const fb = (i > 0 && subsequentFallback) ? subsequentFallback : fallback;
    const start = fb.start || "";
    return { date: dt, start, end: fb.end || "", meet_time: start ? plusOneHour(start) : "", meet_location: "", comp: 0, hours: tourHoursBetween(start, fb.end || "") };
  });
};

// Match a day_shifts entry to a date regardless of format ("M/D/YYYY" vs "YYYY-MM-DD").
const _dayKey = (s) => {
  if (!s) return null;
  let m = String(s).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) { const y = m[3].length === 2 ? "20" + m[3] : m[3]; return `${y}-${String(+m[1]).padStart(2, "0")}-${String(+m[2]).padStart(2, "0")}`; }
  m = String(s).match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${String(+m[2]).padStart(2, "0")}-${String(+m[3]).padStart(2, "0")}`;
  return null;
};
const _findDay = (tour, dateStr) => {
  const k = _dayKey(dateStr);
  return (tour.day_shifts || []).find((d) => d && (d.date === dateStr || (k && _dayKey(d.date) === k)));
};

// The shift for a specific date on a tour; falls back to the tour's single shift fields.
export const dayShiftFor = (tour, dateStr) => {
  const ds = _findDay(tour, dateStr);
  if (ds) return { start: ds.start, end: ds.end };
  return { start: tour.shift_start, end: tour.shift_end };
};

// Full per-day schedule (times + pickup + location) for a specific date on a multi-day tour,
// falling back to the tour's top-level fields. Use this anywhere a single date is shown so
// multi-day tours never display Day 1's info on Day 2.
export const dayScheduleFor = (tour, dateStr) => {
  const ds = _findDay(tour, dateStr) || {};
  return {
    start: ds.start || tour.shift_start,
    end: ds.end || tour.shift_end,
    meet_time: ds.meet_time || tour.meeting_time,
    meet_location: ds.meet_location || tour.resort_meet,
  };
};

// All booking statuses (also selectable manually in the dropdown).
export const ALL_STATUSES = [
  "Current", "Complete", "Past (Update)", "Future (Scheduled)", "Future (Request)",
];

// Auto booking status from tour dates, mirroring the backend. Any explicit status
// chosen in status_override wins over the auto date logic.
// Midnight today, anchored to WDW / Eastern time (so day boundaries match Florida, not the device).
export const etTodayMidnight = () => {
  const p = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const o = {};
  p.forEach((x) => { o[x.type] = x.value; });
  const d = new Date(+o.year, +o.month - 1, +o.day);
  d.setHours(0, 0, 0, 0);
  return d;
};

const parseTourDateList = (f) => {
  const td = Array.isArray(f.tour_date) ? f.tour_date : f.tour_date ? [f.tour_date] : [];
  return td
    .map((s) => {
      const m = String(s).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      if (!m) return null;
      const y = m[3].length === 2 ? "20" + m[3] : m[3];
      const d = new Date(+y, +m[1] - 1, +m[2]);
      d.setHours(0, 0, 0, 0);
      return isNaN(d.getTime()) ? null : d;
    })
    .filter(Boolean);
};

// True when today (Eastern) falls within the tour's date span — regardless of completion status.
export const spansToday = (f) => {
  const parsed = parseTourDateList(f);
  if (!parsed.length) return false;
  const today = etTodayMidnight();
  return new Date(Math.min(...parsed)) <= today && today <= new Date(Math.max(...parsed));
};

export const deriveStatus = (f) => {
  const ov = f.status_override === "Future (Possible)" ? "Future (Request)" : f.status_override;
  if (ALL_STATUSES.includes(ov)) return ov;
  const parsed = parseTourDateList(f);
  if (!parsed.length) return "Future (Request)";
  const today = etTodayMidnight();
  const start = new Date(Math.min(...parsed));
  const end = new Date(Math.max(...parsed));
  if (end < today) return "Past (Update)";
  if (start <= today && today <= end) return "Current";
  return "Future (Scheduled)";
};

// Parse tour_date (string or array of "M/D/YYYY") into normalized Date objects.
export const parseTourDates = (td) => {
  const arr = Array.isArray(td) ? td : td ? [td] : [];
  return arr
    .map((s) => {
      const m = String(s).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      if (!m) return null;
      const y = m[3].length === 2 ? "20" + m[3] : m[3];
      const d = new Date(+y, +m[1] - 1, +m[2]);
      d.setHours(0, 0, 0, 0);
      return isNaN(d.getTime()) ? null : d;
    })
    .filter(Boolean);
};

// [from, to] Date bounds for a named range (all/week/month/custom), or null for "all".
export const rangeBounds = (range, customFrom, customTo) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (range === "last7" || range === "last30") {
    const days = range === "last7" ? 7 : 30;
    const from = new Date(today);
    from.setDate(today.getDate() - (days - 1));
    const to = new Date(today);
    to.setHours(23, 59, 59, 999);
    return [from, to];
  }
  if (range === "week") {
    const from = new Date(today);
    from.setDate(today.getDate() - today.getDay());
    const to = new Date(from);
    to.setDate(from.getDate() + 6);
    to.setHours(23, 59, 59, 999);
    return [from, to];
  }
  if (range === "month") {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    to.setHours(23, 59, 59, 999);
    return [from, to];
  }
  if (range === "year") {
    const from = new Date(today.getFullYear(), 0, 1);
    const to = new Date(today.getFullYear(), 11, 31);
    to.setHours(23, 59, 59, 999);
    return [from, to];
  }
  if (range === "lastyear") {
    const from = new Date(today.getFullYear() - 1, 0, 1);
    const to = new Date(today.getFullYear() - 1, 11, 31);
    to.setHours(23, 59, 59, 999);
    return [from, to];
  }
  if (range === "custom") {
    const from = customFrom ? new Date(customFrom + "T00:00:00") : null;
    const to = customTo ? new Date(customTo + "T00:00:00") : null;
    if (to) to.setHours(23, 59, 59, 999);
    return [from, to];
  }
  return null;
};

export const statusColor = (s) =>  ({
    Current: "bg-[#9B2226]/15 text-[#E0575B]",
    Complete: "bg-emerald-500/12 text-emerald-300",
    "Past (Update)": "bg-amber-500/12 text-amber-300",
    "Future (Scheduled)": "bg-zinc-700/40 text-zinc-300",
    "Future (Request)": "bg-indigo-500/14 text-indigo-300",
    "Future (Possible)": "bg-indigo-500/14 text-indigo-300",
  }[s] || "bg-zinc-700/40 text-zinc-300");
